import {
  Card,
  CardContent,
  Checkbox,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from "@material-ui/core";
import CardTitle from "@saleor/components/CardTitle";
import { useTemplateStyles } from "@saleor/dropOff/styles/templates";
import { TemplateAction } from "@saleor/dropOff/utils/handlers";
import { maybe } from "@saleor/misc";
import { ListSettings } from "@saleor/types";
import React from "react";

import DropOffSearchFilter from "../DropOffSearchFilter";
import DropOffSearchInput from "../DropOffSearchInput";
import DropOffTablePagination from "../DropOffTablePagination";

export interface DropOffTaskAreaAssignedStoresProps {
  assignedStores: any[];
  setAssignedStores: (value: React.SetStateAction<any[]>) => void;

  searchAssignedStores: string;
  onSearchChangeAssignedStores: (text: string) => void;
  onFilterChangeAssignedStores: (index: number) => void;

  selectedTaskArea: any;
  isSelectAllAssignedStores: boolean;
  disableSelectAllAssignedStores: boolean;
  clickSelectAllAssignedStores: () => void;

  templateLoading: boolean;
  templateSettings: ListSettings<string>;
  templateUpdateListSettings: (
    key: keyof ListSettings<any>,
    value: any
  ) => void;
  templatePageInfo: any;
  onTemplateLoadNextPage: () => void;
  onTemplateLoadPreviousPage: () => void;
  templateAction: TemplateAction;
}

const DropOffTaskAreaAssignedStores: React.FC<DropOffTaskAreaAssignedStoresProps> = props => {
  const {
    assignedStores,
    setAssignedStores,
    searchAssignedStores,
    onSearchChangeAssignedStores,
    onFilterChangeAssignedStores,
    selectedTaskArea,
    isSelectAllAssignedStores,
    disableSelectAllAssignedStores,
    clickSelectAllAssignedStores,
    templateLoading,
    templateSettings,
    templateUpdateListSettings,
    templatePageInfo,
    onTemplateLoadNextPage,
    onTemplateLoadPreviousPage,
    templateAction
  } = props;

  const classes = useTemplateStyles(props);

  const options = ["All", "Food & Beverages", "General Merchandise"];

  const handleCheckboxClick = (store, tenant) => {
    const newState = assignedStores.map(obj => {
      if (obj.id === store.id) {
        const updatedTenants = obj.tenants.map(o => {
          if (o.id === tenant.id) {
            return { ...o, isChecked: !o.isChecked };
          }
          return o;
        });
        return { ...obj, tenants: updatedTenants };
      }
      return obj;
    });

    setAssignedStores(newState);
  };

  const renderStoreList = (store, templateAction) =>
    maybe(() => selectedTaskArea?.id === store.id) && (
      <List dense component="div" role="ul" key={"assigned-" + store.id}>
        {store.tenants.map(tenant => (
          <ListItem
            key={"assigned-" + tenant?.tenantId}
            role="li"
            button
            onClick={() =>
              templateAction === TemplateAction.VIEW
                ? null
                : handleCheckboxClick(store, tenant)
            }
          >
            <ListItemText id={tenant.id} primary={tenant.storeName} />

            {templateAction === TemplateAction.VIEW ? null : (
              <ListItemIcon className={classes.cardCheckbox}>
                <Checkbox checked={tenant.isChecked} disableRipple />
              </ListItemIcon>
            )}
          </ListItem>
        ))}
      </List>
    );

  return (
    <Grid
      {...(templateAction === TemplateAction.VIEW ? {} : { xs: 5, item: true })}
    >
      <div
        className={`${classes.displayFlexCenterSpaceBetween} ${classes.assignStoreSearchFilter}`}
      >
        <div className={classes.div100}>
          <DropOffSearchInput
            key="dropOffAssignTASearchAssignedStores"
            initialSearch={searchAssignedStores}
            placeholder="Search Assigned Stores"
            onSearchChange={onSearchChangeAssignedStores}
          />
        </div>
        <div className={classes.textMarginLeft}>
          <DropOffSearchFilter
            keyId="dropOffAssignTAFilterAssignedStores"
            options={options}
            handleChange={onFilterChangeAssignedStores}
          />
        </div>
      </div>

      {templateLoading ? (
        <Card className={classes.taskAreaCard}>
          <div className={classes.divFlexCentered100}>
            <span className={classes.textPrimary}>
              Loading Assigned Stores...
            </span>
          </div>
        </Card>
      ) : (
        <Card className={classes.taskAreaCard}>
          <CardTitle title={null}>
            <Grid
              container
              direction="row"
              justify="space-between"
              alignItems="center"
              className={classes.cardTitleContainer}
            >
              {templateAction === TemplateAction.VIEW ? (
                <></>
              ) : (
                <>
                  <Grid item>
                    <span className={classes.cardTitle}>Select All</span>
                  </Grid>
                  <Grid item>
                    <Checkbox
                      disabled={
                        assignedStores.length === 0 ||
                        disableSelectAllAssignedStores
                      }
                      onChange={() => clickSelectAllAssignedStores()}
                      checked={isSelectAllAssignedStores}
                      tabIndex={-1}
                      disableRipple
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </CardTitle>
          <CardContent className={classes.taskAreaCardContent}>
            {assignedStores.map(store =>
              renderStoreList(store, templateAction)
            )}
          </CardContent>
        </Card>
      )}

      <DropOffTablePagination
        settings={templateSettings}
        onUpdateListSettings={templateUpdateListSettings}
        hasNextPage={
          templatePageInfo && !templateLoading
            ? templatePageInfo.hasNextPage
            : false
        }
        onNextPage={onTemplateLoadNextPage}
        hasPreviousPage={
          templatePageInfo && !templateLoading
            ? templatePageInfo.hasPreviousPage
            : false
        }
        onPreviousPage={onTemplateLoadPreviousPage}
      />
    </Grid>
  );
};

DropOffTaskAreaAssignedStores.displayName = "DropOffTaskAreaAssignedStores";
export default DropOffTaskAreaAssignedStores;
